import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, CheckboxField, SearchField, Text } from '@frontend/design-system';
import { AxisGroup, AxisOption } from './types';
import { useCheckboxGroupState } from './use-checkbox-group-state';

// ── CheckboxOption (single row) ──────────────────────────────────────

const CheckboxOption = ({
  option,
  checked,
  indeterminate,
  disabled,
  onChange,
  isChild,
}: {
  option: AxisOption;
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
  isChild?: boolean;
}) => {
  const dummyProps = {
    id: `checkbox-${option.value || option.label}`,
    onBlur: () => {},
    onFocus: () => {},
    error: '',
    touched: false,
    'aria-invalid': false,
    active: false,
  };

  return (
    <div css={[styles.optionRow, isChild && styles.childOption]}>
      <CheckboxField
        {...dummyProps}
        name={option.value || option.label}
        value={checked || indeterminate}
        indeterminate={indeterminate}
        disabled={disabled}
        onChange={(e: any) => {
          const val = typeof e === 'boolean' ? e : e && typeof e.value === 'boolean' ? e.value : !!e?.target?.checked;
          onChange(val);
        }}
        label={option.renderOption ? option.renderOption(option, checked) : <Text size='large'>{option.label}</Text>}
      />
    </div>
  );
};

// ── CheckboxGroup ────────────────────────────────────────────────────

export const CheckboxGroup = ({
  group,
  draftY,
  onCheckboxChange,
  disabledOptions = [],
}: {
  group: AxisGroup;
  draftY: string[];
  onCheckboxChange: (values: string[]) => void;
  disabledOptions?: string[];
}) => {
  const { t } = useTranslation('analytics');
  const {
    searchField,
    filteredOptions,
    filteredLeafValues,
    selectedInGroup,
    handleSelectAll,
    handleClear,
    handleOptionChange,
    getOptionState,
  } = useCheckboxGroupState({ group, draftY, onCheckboxChange });

  const renderOption = (option: AxisOption, isChild = false) => {
    const { checked, indeterminate } = getOptionState(option);

    return (
      <CheckboxOption
        key={option.value || option.label}
        option={option}
        checked={checked}
        indeterminate={indeterminate}
        disabled={disabledOptions.includes(option.value)}
        onChange={(val) => handleOptionChange(option, val)}
        isChild={isChild}
      />
    );
  };

  const optionList = (
    <div css={[styles.checklistField, group.searchable && styles.scrollableList]}>
      {filteredOptions.length === 0 ? (
        <Text size='medium' color='light' css={styles.noResults}>
          {t('No results found')}
        </Text>
      ) : (
        filteredOptions.map((option) => (
          <div key={option.value || option.label}>
            {renderOption(option)}
            {option.children && option.children.map((child) => renderOption(child, true))}
          </div>
        ))
      )}
    </div>
  );

  if (!group.searchable) {
    return optionList;
  }

  return (
    <div css={styles.searchableContainer}>
      {/* Search input */}
      <div css={styles.searchContainer} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <SearchField
          {...searchField}
          name='search'
          placeholder={t('Search')}
          autoFocus
          onKeyDown={(e) => {
            const navKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Tab'];
            if (!navKeys.includes(e.key)) e.stopPropagation();
          }}
        />
      </div>

      {/* Select All / Clear actions */}
      <div css={styles.actions}>
        <Button variant='tertiary' size='small' onClick={handleSelectAll} disabled={filteredLeafValues.length === 0}>
          {t('Select All')}
        </Button>
        <Button variant='tertiary' size='small' onClick={handleClear} disabled={selectedInGroup.length === 0}>
          {t('Clear')}
        </Button>
      </div>

      {optionList}
    </div>
  );
};

// ── Styles ───────────────────────────────────────────────────────────

const styles = {
  checklistField: css({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  }),
  optionRow: css({
    display: 'flex',
    alignItems: 'center',
    '> div': {
      alignItems: 'center',
    },
  }),
  childOption: css({
    marginLeft: theme.spacing(3),
    marginTop: theme.spacing(1),
  }),
  // Search-related styles (only used when group.searchable is true)
  searchableContainer: css({
    display: 'flex',
    flexDirection: 'column',
    width: 260,
    minHeight: 380,
  }),
  searchContainer: css({
    padding: `0 ${theme.spacing(0.5)} ${theme.spacing(1)}`,
  }),
  actions: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `0 ${theme.spacing(0.5)} ${theme.spacing(0.5)}`,
  }),
  scrollableList: css({
    maxHeight: 300,
    overflowY: 'auto',
    padding: `${theme.spacing(0.5)} 0`,
  }),
  noResults: css({
    padding: `${theme.spacing(2)} ${theme.spacing(1)}`,
    textAlign: 'center',
  }),
};
